import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ReactComponent as CalendarIcon } from 'assets/images/calendar.svg';
import { ReactComponent as ClockIcon } from 'assets/images/clock.svg';
import { ReactComponent as InfoIcon } from 'assets/images/Info.svg';

import {
  InfoButton,
  TooltipIcon,
  TooltipLabel,
  TooltipPanel,
  TooltipRow,
  TooltipRows,
  TooltipText,
  TooltipTitle,
  TriggerWrap,
} from './styles';

const VIEWPORT_MARGIN = 16;
const TOOLTIP_GAP = 10;
const MAX_TOOLTIP_WIDTH = 328;

type TooltipPosition = {
  top: number;
  left: number;
  mobile: boolean;
  arrowOffset: number;
};

export type StatusCompensationTooltipProps = {
  compact?: boolean;
};

const StatusCompensationTooltip = ({
  compact = false,
}: StatusCompensationTooltipProps): JSX.Element => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
    mobile: false,
    arrowOffset: 0,
  });

  const updatePosition = useCallback((): void => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();

    if (compact) {
      const viewportWidth = window.innerWidth;
      const panelWidth = Math.min(
        MAX_TOOLTIP_WIDTH,
        viewportWidth - VIEWPORT_MARGIN * 2
      );
      const iconCenterX = rect.left + rect.width / 2;
      const minCenter = VIEWPORT_MARGIN + panelWidth / 2;
      const maxCenter = viewportWidth - VIEWPORT_MARGIN - panelWidth / 2;
      const clampedCenter = Math.min(
        Math.max(iconCenterX, minCenter),
        maxCenter
      );

      setPosition({
        top: rect.bottom + TOOLTIP_GAP,
        left: clampedCenter,
        mobile: true,
        arrowOffset: iconCenterX - clampedCenter,
      });
      return;
    }

    setPosition({
      top: rect.top + rect.height / 2,
      left: rect.left - TOOLTIP_GAP,
      mobile: false,
      arrowOffset: 0,
    });
  }, [compact]);

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();

    const handleReposition = (): void => {
      updatePosition();
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerOutside = (event: PointerEvent): void => {
      const target = event.target as HTMLElement | null;

      if (!target?.closest('[data-status-compensation-tooltip]')) {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerOutside);

    return () => document.removeEventListener('pointerdown', handlePointerOutside);
  }, [open]);

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setOpen((current) => !current);
  };

  return (
    <TriggerWrap data-status-compensation-tooltip>
      <InfoButton
        ref={buttonRef}
        type="button"
        aria-label="Tempo de compensação do pagamento"
        aria-expanded={open}
        onClick={handleToggle}
      >
        <InfoIcon aria-hidden="true" />
      </InfoButton>

      {open &&
        createPortal(
          <TooltipPanel
            data-status-compensation-tooltip
            role="tooltip"
            $top={position.top}
            $left={position.left}
            $mobile={position.mobile}
            $arrowOffset={position.arrowOffset}
          >
            <TooltipTitle>Tempo de compensação do pagamento</TooltipTitle>
            <TooltipRows>
              <TooltipRow $mobile={position.mobile}>
                <TooltipLabel>Boleto -</TooltipLabel>
                <TooltipIcon aria-hidden="true">
                  <CalendarIcon />
                </TooltipIcon>
                <TooltipText $mobile={position.mobile}>
                  3 dias úteis após o pagamento
                </TooltipText>
              </TooltipRow>
              <TooltipRow $mobile={position.mobile}>
                <TooltipLabel>PIX -</TooltipLabel>
                <TooltipIcon aria-hidden="true">
                  <ClockIcon />
                </TooltipIcon>
                <TooltipText $mobile={position.mobile}>
                  2 minutos após o pagamento
                </TooltipText>
              </TooltipRow>
            </TooltipRows>
          </TooltipPanel>,
          document.body
        )}
    </TriggerWrap>
  );
};

export default StatusCompensationTooltip;
